import FilterChipRow from './FilterChipRow.tsx';
import { activeFilterChips, binChips, binLine, showingLine, type FilterChipAxis } from './activeFilterChips.ts';
import type { DeckFilterState } from './deckFilterState.ts';
import type { DeckModelOption } from './useDeckFilters.ts';
import type { CategoryColour } from '../../../shared/api.ts';

/**
 * The decks page's chip row: its state turned into chips, handed to the shared
 * `FilterChipRow`.
 *
 * The row itself moved into that component in Run 9 so the Library could use
 * it without owning a second copy; what stays here is the only deck-specific
 * half — which chips exist, and the noun in "Showing 12 of 62 decks".
 */

type Props = {
  filters: DeckFilterState;
  models: readonly DeckModelOption[];
  categoryColour?: (path: string) => CategoryColour | null;
  /** Turns an owner id into a name for the Owner chip. */
  personName?: (id: string) => string | null;
  shown: number;
  total: number;
  /** How many decks are in the bin. Only read in the trash view, where the
   *  row speaks for the bin rather than for the grid. */
  trashCount?: number;
  /** Clear one axis. The route owns which setter that is. */
  onClearAxis: (axis: FilterChipAxis) => void;
  onClearAll: () => void;
};

export default function ActiveFilterRow({
  filters,
  models,
  categoryColour,
  personName,
  shown,
  total,
  trashCount = 0,
  onClearAxis,
  onClearAll,
}: Props) {
  // In the bin the row describes the bin: see `binLine` / `binChips`.
  const inBin = filters.status === 'trash';
  const chips = activeFilterChips({ filters, models, categoryColour, personName });
  return (
    <FilterChipRow<FilterChipAxis>
      chips={inBin ? binChips(chips) : chips}
      showing={inBin ? binLine(trashCount) : showingLine(shown, total)}
      onClearAxis={onClearAxis}
      onClearAll={onClearAll}
    />
  );
}
